import { useCallback, useEffect, useId, useRef, useState, type DragEvent } from "react";
import { ChevronDown, ChevronRight, GripVertical, Plus, Trash2 } from "lucide-react";
import type { ItemArrayMeta, ColumnMeta } from "@sections/itemMeta";
import { ImageUploadField } from "./ImageUploadField";
import { getCropConstraint } from "./cropConstraints";

/* ── Prop classification helpers ───────────────────────────── */

/** Props that hold image URLs and should render an upload field. */
const IMAGE_ITEM_PROPS = new Set(["avatarUrl", "photoUrl", "imageUrl"]);

/** Props that hold icon/emoji values and get a special text input. */
const ICON_ITEM_PROPS = new Set(["icon"]);

/** Internal-only props that should never render an editable field. */
const HIDDEN_ITEM_PROPS = new Set([
  "key", "id",
  /* Crop metadata -- managed by ImageUploadField internally */
  "imageOriginalUrl", "imageOriginalStorageKey", "imageCrop",
  "photoOriginalUrl", "photoOriginalStorageKey", "photoCrop",
  "avatarOriginalUrl", "avatarOriginalStorageKey", "avatarCrop",
]);

/**
 * Derives crop metadata field names from an image field name.
 * e.g. "photoUrl" -> { originalUrl: "photoOriginalUrl", ... }
 */
function cropMetaFieldNames(imageField: string) {
  const base = imageField.replace(/Url$/, "");
  return {
    originalUrl: `${base}OriginalUrl`,
    originalStorageKey: `${base}OriginalStorageKey`,
    crop: `${base}Crop`,
  };
}

/* ── Types ─────────────────────────────────────────────────── */

interface ItemListEditorProps {
  sectionType: string;
  items: Record<string, unknown>[];
  meta: ItemArrayMeta;
  columnMeta?: ColumnMeta;
  columnHeaders?: string[];
  onUpdate: (newItems: Record<string, unknown>[]) => void;
  onUpdateColumns?: (newHeaders: string[]) => void;
  /** Canvas click-to-focus: expand and scroll to a specific item. */
  focusItemRequest?: { arrayProp: string; index: number } | null;
  /** Clear the focus request after handling it. */
  onClearFocusRequest?: () => void;
}

/* ── Component ─────────────────────────────────────────────── */

export function ItemListEditor({
  sectionType,
  items,
  meta,
  columnMeta,
  columnHeaders,
  onUpdate,
  onUpdateColumns,
  focusItemRequest,
  onClearFocusRequest,
}: ItemListEditorProps) {
  const idBase = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  /* ── Drag state (HTML5 DnD following FooterEditor pattern) ── */
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [hoverPos, setHoverPos] = useState<"above" | "below">("below");
  const dragCounter = useRef(0);

  const onDragStart = useCallback(
    (e: DragEvent<HTMLSpanElement>, index: number) => {
      setDragIndex(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));
    },
    [],
  );

  const onDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      const rect = e.currentTarget.getBoundingClientRect();
      setHoverPos(e.clientY < rect.top + rect.height / 2 ? "above" : "below");
      setHoverIndex(index);
    },
    [],
  );

  const onDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      dragCounter.current++;
      setHoverIndex(index);
    },
    [],
  );

  const onDragLeave = useCallback(() => {
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      setHoverIndex(null);
      dragCounter.current = 0;
    }
  }, []);

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>, dropIndex: number) => {
      e.preventDefault();
      dragCounter.current = 0;
      if (dragIndex === null || dragIndex === dropIndex) {
        setDragIndex(null);
        setHoverIndex(null);
        return;
      }
      const next = [...items];
      const [moved] = next.splice(dragIndex, 1);
      let toIndex = dropIndex;
      if (hoverPos === "below" && dropIndex < dragIndex) toIndex++;
      else if (hoverPos === "above" && dropIndex > dragIndex) toIndex--;
      toIndex = Math.max(0, Math.min(next.length, toIndex));
      next.splice(toIndex, 0, moved);
      onUpdate(next);
      // Update expanded index to follow the item
      if (expandedIndex === dragIndex) {
        setExpandedIndex(toIndex);
      } else if (expandedIndex !== null) {
        // Adjust expanded index if it shifted
        if (dragIndex < expandedIndex && toIndex >= expandedIndex) {
          setExpandedIndex(expandedIndex - 1);
        } else if (dragIndex > expandedIndex && toIndex <= expandedIndex) {
          setExpandedIndex(expandedIndex + 1);
        }
      }
      setDragIndex(null);
      setHoverIndex(null);
    },
    [dragIndex, hoverPos, items, onUpdate, expandedIndex],
  );

  const onDragEnd = useCallback(() => {
    setDragIndex(null);
    setHoverIndex(null);
    dragCounter.current = 0;
  }, []);

  /* ── CRUD operations ────────────────────────────────────── */

  const addItem = useCallback(() => {
    if (items.length >= meta.maxItems) return;
    let newItem = { ...meta.itemDefaults };
    // For ComparisonTable rows, match the values array to column count
    if (meta.prop === "rows" && columnHeaders) {
      newItem = { ...newItem, values: columnHeaders.map(() => true) };
    }
    const next = [...items, newItem];
    onUpdate(next);
    // Auto-expand and scroll to the new item
    const newIndex = next.length - 1;
    setExpandedIndex(newIndex);
    requestAnimationFrame(() => {
      const el = listRef.current?.querySelector(`[data-item-index="${newIndex}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, [items, meta, columnHeaders, onUpdate]);

  const removeItem = useCallback(
    (index: number) => {
      if (items.length <= meta.minItems) return;
      const next = items.filter((_, i) => i !== index);
      onUpdate(next);
      // Adjust expanded index
      if (expandedIndex === index) {
        setExpandedIndex(null);
      } else if (expandedIndex !== null && expandedIndex > index) {
        setExpandedIndex(expandedIndex - 1);
      }
    },
    [items, meta.minItems, onUpdate, expandedIndex],
  );

  const updateItemField = useCallback(
    (index: number, field: string, value: unknown) => {
      const next = items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      );
      onUpdate(next);
    },
    [items, onUpdate],
  );

  const toggleExpand = useCallback(
    (index: number) => {
      setExpandedIndex((prev) => (prev === index ? null : index));
    },
    [],
  );

  /* ── Column operations (ComparisonTable only) ──────────── */

  const addColumn = useCallback(() => {
    if (!columnMeta || !columnHeaders || !onUpdateColumns) return;
    if (columnHeaders.length >= columnMeta.maxColumns) return;
    const newHeaders = [...columnHeaders, columnMeta.defaultHeader];
    onUpdateColumns(newHeaders);
    // Also add a value to each row
    const newItems = items.map((item) => {
      const values = Array.isArray(item.values) ? [...(item.values as unknown[])] : [];
      values.push(true);
      return { ...item, values };
    });
    onUpdate(newItems);
  }, [columnMeta, columnHeaders, onUpdateColumns, items, onUpdate]);

  const removeColumn = useCallback(
    (colIndex: number) => {
      if (!columnMeta || !columnHeaders || !onUpdateColumns) return;
      if (columnHeaders.length <= columnMeta.minColumns) return;
      const newHeaders = columnHeaders.filter((_, i) => i !== colIndex);
      onUpdateColumns(newHeaders);
      // Also remove the value at colIndex from each row
      const newItems = items.map((item) => {
        const values = Array.isArray(item.values) ? [...(item.values as unknown[])] : [];
        values.splice(colIndex, 1);
        return { ...item, values };
      });
      onUpdate(newItems);
    },
    [columnMeta, columnHeaders, onUpdateColumns, items, onUpdate],
  );

  const updateColumnHeader = useCallback(
    (colIndex: number, value: string) => {
      if (!columnHeaders || !onUpdateColumns) return;
      const newHeaders = columnHeaders.map((h, i) => (i === colIndex ? value : h));
      onUpdateColumns(newHeaders);
    },
    [columnHeaders, onUpdateColumns],
  );

  /* ── Canvas click-to-focus ───────────────────────────────── */

  useEffect(() => {
    if (
      focusItemRequest &&
      focusItemRequest.arrayProp === meta.prop &&
      focusItemRequest.index >= 0 &&
      focusItemRequest.index < items.length
    ) {
      setExpandedIndex(focusItemRequest.index);
      requestAnimationFrame(() => {
        const el = listRef.current?.querySelector(
          `[data-item-index="${focusItemRequest.index}"]`,
        );
        el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
      onClearFocusRequest?.();
    }
  }, [focusItemRequest, meta.prop, items.length, onClearFocusRequest]);

  /* ── Derive item fields from the first item or defaults ── */

  const itemFields = Object.keys(meta.itemDefaults).filter(
    (k) => !HIDDEN_ITEM_PROPS.has(k),
  );

  // For Testimonials, include optional fields that may not be in defaults
  // but exist in the schema. We infer from the first existing item too.
  const allFieldKeys = new Set(itemFields);
  if (items.length > 0) {
    for (const key of Object.keys(items[0])) {
      if (!HIDDEN_ITEM_PROPS.has(key)) allFieldKeys.add(key);
    }
  }
  // For sections with optional fields not in defaults, add known schema fields
  const knownOptionalFields: Record<string, string[]> = {
    Testimonials: ["role", "avatarUrl", "rating"],
    TeamGrid: ["photoUrl", "bio"],
    LogoStrip: ["imageUrl", "href"],
    CTACards: ["icon"],
    FeatureGrid: ["icon"],
    FeatureList: ["icon"],
    StatsBar: ["prefix", "suffix"],
    FeatureShowcase: ["imageUrl", "ctaLabel", "ctaHref"],
  };
  for (const f of knownOptionalFields[sectionType] ?? []) {
    allFieldKeys.add(f);
  }
  const fieldKeys = Array.from(allFieldKeys);

  /* ── Field label helper ─────────────────────────────────── */

  function fieldLabel(key: string): string {
    switch (key) {
      case "ctaLabel": return "Button label";
      case "ctaHref": return "Button link";
      case "avatarUrl": return "Avatar";
      case "photoUrl": return "Photo";
      case "imageUrl": return "Image";
      case "bio": return "Bio";
      case "href": return "Link URL";
      case "icon": return "Icon / emoji";
      case "prefix": return "Prefix";
      case "suffix": return "Suffix";
      default: return key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1");
    }
  }

  /* ── Render ──────────────────────────────────────────────── */

  const atMin = items.length <= meta.minItems;
  const atMax = items.length >= meta.maxItems;
  const capLabel = meta.itemLabel.charAt(0).toUpperCase() + meta.itemLabel.slice(1);

  return (
    <div className="mb-[18px]">
      <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
        {capLabel}s ({items.length})
      </label>

      {/* Column headers (ComparisonTable only) */}
      {columnMeta && columnHeaders && onUpdateColumns && (
        <div className="mb-3">
          <div className="text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[5px]">
            Columns ({columnHeaders.length})
          </div>
          <div className="space-y-1.5">
            {columnHeaders.map((header, ci) => (
              <div key={ci} className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={header}
                  onChange={(e) => updateColumnHeader(ci, e.target.value)}
                  className="flex-1 border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                  placeholder="Column name"
                />
                <button
                  type="button"
                  onClick={() => removeColumn(ci)}
                  disabled={columnHeaders.length <= columnMeta.minColumns}
                  title={
                    columnHeaders.length <= columnMeta.minColumns
                      ? `At least ${columnMeta.minColumns} columns required`
                      : `Remove column`
                  }
                  className="flex-none p-1 rounded text-ink-5 hover:text-rust hover:bg-rust-s disabled:opacity-30 disabled:hover:text-ink-5 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
                >
                  <Trash2 size={12} strokeWidth={1.8} />
                </button>
              </div>
            ))}
          </div>
          {columnHeaders.length < columnMeta.maxColumns && (
            <button
              type="button"
              onClick={addColumn}
              className="mt-1.5 flex items-center gap-1 text-[11.5px] font-medium text-tide hover:text-tide-d transition-colors"
            >
              <Plus size={12} strokeWidth={2.5} />
              Add column
            </button>
          )}
          {columnHeaders.length >= columnMeta.maxColumns && (
            <p className="mt-1 text-[10.5px] text-ink-5">
              Maximum of {columnMeta.maxColumns} columns.
            </p>
          )}
          <div className="mt-2.5 mb-1 border-t border-ink-6" />
        </div>
      )}

      {/* Item list */}
      <div ref={listRef} className="space-y-1">
        {items.map((item, index) => {
          const isExpanded = expandedIndex === index;
          const isDragging = dragIndex === index;
          const isHovered = hoverIndex === index && dragIndex !== null && dragIndex !== index;
          const summaryValue = String(item[meta.summaryProp] ?? "").replace(/<[^>]*>/g, "");
          const summaryLabel = summaryValue.length > 30
            ? summaryValue.slice(0, 30) + "..."
            : summaryValue || `${capLabel} ${index + 1}`;

          return (
            <div
              key={index}
              data-item-index={index}
              onDragOver={(e) => onDragOver(e, index)}
              onDragEnter={(e) => onDragEnter(e, index)}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, index)}
              onDragEnd={onDragEnd}
              className={`relative rounded-[9px] border bg-paper transition ${
                isDragging ? "opacity-40" : ""
              } ${isExpanded ? "border-ink-5" : "border-ink-6 hover:border-ink-5"}`}
            >
              {/* Drop indicators */}
              {isHovered && hoverPos === "above" && (
                <span className="absolute -top-[1px] left-3 right-3 h-[2px] rounded-full bg-tide" />
              )}
              {isHovered && hoverPos === "below" && (
                <span className="absolute -bottom-[1px] left-3 right-3 h-[2px] rounded-full bg-tide" />
              )}

              {/* Collapsed row: drag handle + number + summary + expand + delete */}
              <div className="flex items-center gap-1.5 px-2 py-[7px]">
                <span
                  className="flex-none cursor-grab text-ink-5 active:cursor-grabbing"
                  draggable
                  onDragStart={(e) => onDragStart(e, index)}
                >
                  <GripVertical size={12} strokeWidth={2} />
                </span>
                <span className="flex-none text-[10.5px] font-semibold text-ink-5 tabular-nums w-4 text-center">
                  {index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => toggleExpand(index)}
                  className="flex-1 min-w-0 flex items-center gap-1 bg-transparent border-0 cursor-pointer text-left p-0"
                >
                  {isExpanded ? (
                    <ChevronDown size={12} strokeWidth={2} className="flex-none text-ink-4" />
                  ) : (
                    <ChevronRight size={12} strokeWidth={2} className="flex-none text-ink-4" />
                  )}
                  <span className="text-[12px] text-ink-2 truncate">
                    {summaryLabel}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  disabled={atMin}
                  title={
                    atMin
                      ? `At least ${meta.minItems} ${meta.itemLabel}${meta.minItems !== 1 ? "s" : ""} required`
                      : `Remove ${meta.itemLabel}`
                  }
                  className="flex-none p-1 rounded text-ink-5 hover:text-rust hover:bg-rust-s disabled:opacity-30 disabled:hover:text-ink-5 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
                >
                  <Trash2 size={12} strokeWidth={1.8} />
                </button>
              </div>

              {/* Expanded: editable fields */}
              {isExpanded && (
                <div className="px-3 pb-3 pt-0.5 space-y-2.5 border-t border-ink-6">
                  {fieldKeys.map((fieldKey) => {
                    if (HIDDEN_ITEM_PROPS.has(fieldKey)) return null;

                    // Image upload fields
                    if (IMAGE_ITEM_PROPS.has(fieldKey)) {
                      const cropMeta = cropMetaFieldNames(fieldKey);
                      const constraint = getCropConstraint(sectionType, fieldKey);
                      return (
                        <div key={fieldKey} className="mt-2">
                          <ImageUploadField
                            label={fieldLabel(fieldKey)}
                            value={(item[fieldKey] as string) || undefined}
                            onChange={(url) => updateItemField(index, fieldKey, url || undefined)}
                            cropConstraint={constraint}
                            imageOriginalUrl={item[cropMeta.originalUrl] as string | undefined}
                            imageOriginalStorageKey={item[cropMeta.originalStorageKey] as string | undefined}
                            imageCrop={
                              item[cropMeta.crop] as
                                | { x: number; y: number; width: number; height: number; zoom: number }
                                | undefined
                            }
                            onCropMetadataChange={(meta) => {
                              const next = { ...item };
                              next[cropMeta.originalUrl] = meta.imageOriginalUrl;
                              next[cropMeta.originalStorageKey] = meta.imageOriginalStorageKey;
                              next[cropMeta.crop] = meta.imageCrop;
                              const newItems = items.map((it, i) =>
                                i === index ? next : it,
                              );
                              onUpdate(newItems);
                            }}
                          />
                        </div>
                      );
                    }

                    // Rating field (number)
                    if (fieldKey === "rating") {
                      return (
                        <div key={fieldKey}>
                          <label
                            htmlFor={`${idBase}-${index}-${fieldKey}`}
                            className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]"
                          >
                            {fieldLabel(fieldKey)}
                          </label>
                          <select
                            id={`${idBase}-${index}-${fieldKey}`}
                            value={item[fieldKey] != null ? String(item[fieldKey]) : ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              updateItemField(index, fieldKey, v ? Number(v) : undefined);
                            }}
                            className="w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                          >
                            <option value="">No rating</option>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <option key={n} value={n}>
                                {"★".repeat(n)} ({n})
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    }

                    // Field type selector (LeadForm custom questions)
                    if (fieldKey === "type" && meta.prop === "fields") {
                      return (
                        <div key={fieldKey}>
                          <label
                            htmlFor={`${idBase}-${index}-${fieldKey}`}
                            className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]"
                          >
                            {fieldLabel(fieldKey)}
                          </label>
                          <select
                            id={`${idBase}-${index}-${fieldKey}`}
                            value={item[fieldKey] != null ? String(item[fieldKey]) : "text"}
                            onChange={(e) => {
                              updateItemField(index, fieldKey, e.target.value);
                            }}
                            className="w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                          >
                            <option value="text">Text</option>
                            <option value="textarea">Text area</option>
                            <option value="select">Select</option>
                          </select>
                        </div>
                      );
                    }

                    // Required toggle (LeadForm custom questions)
                    if (fieldKey === "required" && meta.prop === "fields") {
                      return (
                        <div key={fieldKey} className="flex items-center gap-2 py-1">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={!!item[fieldKey]}
                            onClick={() => updateItemField(index, fieldKey, !item[fieldKey])}
                            className={`relative w-8 h-[18px] rounded-full transition-colors flex-shrink-0 ${
                              item[fieldKey] ? "bg-tide" : "bg-ink-6"
                            }`}
                          >
                            <span
                              className={`absolute top-[2px] left-[2px] w-[14px] h-[14px] rounded-full bg-paper-hi shadow-sm transition-transform ${
                                item[fieldKey] ? "translate-x-[14px]" : ""
                              }`}
                            />
                          </button>
                          <span className="text-[11px] text-ink-3">Required</span>
                        </div>
                      );
                    }

                    // ComparisonTable row values -- render per-column toggles/inputs
                    if (fieldKey === "values" && meta.prop === "rows" && columnHeaders) {
                      const values = Array.isArray(item.values)
                        ? (item.values as (boolean | string)[])
                        : [];
                      return (
                        <div key={fieldKey}>
                          <div className="text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]">
                            Values
                          </div>
                          <div className="space-y-1.5">
                            {columnHeaders.map((header, ci) => {
                              const cellVal = values[ci] ?? true;
                              const isBool = typeof cellVal === "boolean";
                              return (
                                <div key={ci} className="flex items-center gap-2">
                                  <span className="text-[11px] text-ink-4 w-16 truncate flex-none">
                                    {header}
                                  </span>
                                  {isBool ? (
                                    <label className="flex items-center gap-1.5 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={cellVal as boolean}
                                        onChange={(e) => {
                                          const newValues = [...values];
                                          newValues[ci] = e.target.checked;
                                          updateItemField(index, "values", newValues);
                                        }}
                                        className="w-3.5 h-3.5 rounded border-ink-5 text-tide accent-tide cursor-pointer"
                                      />
                                      <span className="text-[11px] text-ink-3">
                                        {cellVal ? "Included" : "Not included"}
                                      </span>
                                    </label>
                                  ) : (
                                    <input
                                      type="text"
                                      value={cellVal as string}
                                      onChange={(e) => {
                                        const newValues = [...values];
                                        newValues[ci] = e.target.value;
                                        updateItemField(index, "values", newValues);
                                      }}
                                      className="flex-1 border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }

                    // Icon/emoji field
                    if (ICON_ITEM_PROPS.has(fieldKey)) {
                      return (
                        <div key={fieldKey}>
                          <label
                            htmlFor={`${idBase}-${index}-${fieldKey}`}
                            className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]"
                          >
                            {fieldLabel(fieldKey)}
                          </label>
                          <input
                            id={`${idBase}-${index}-${fieldKey}`}
                            type="text"
                            value={(item[fieldKey] as string) ?? ""}
                            onChange={(e) => updateItemField(index, fieldKey, e.target.value)}
                            placeholder="e.g. &#x26A1;"
                            className="w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                          />
                        </div>
                      );
                    }

                    // Long text fields (body, bio, quote)
                    if (fieldKey === "body" || fieldKey === "bio" || fieldKey === "quote") {
                      return (
                        <div key={fieldKey}>
                          <label
                            htmlFor={`${idBase}-${index}-${fieldKey}`}
                            className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]"
                          >
                            {fieldLabel(fieldKey)}
                          </label>
                          <textarea
                            id={`${idBase}-${index}-${fieldKey}`}
                            value={(item[fieldKey] as string) ?? ""}
                            onChange={(e) => updateItemField(index, fieldKey, e.target.value)}
                            rows={2}
                            className="w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink resize-y focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                          />
                        </div>
                      );
                    }

                    // Options field (LeadForm select questions) -- only shown
                    // when the question's type is "select", with format hint.
                    if (fieldKey === "options" && meta.prop === "fields") {
                      if (item.type !== "select") return null;
                      return (
                        <div key={fieldKey}>
                          <label
                            htmlFor={`${idBase}-${index}-${fieldKey}`}
                            className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]"
                          >
                            {fieldLabel(fieldKey)}
                          </label>
                          <input
                            id={`${idBase}-${index}-${fieldKey}`}
                            type="text"
                            value={(item[fieldKey] as string) ?? ""}
                            onChange={(e) => updateItemField(index, fieldKey, e.target.value)}
                            placeholder="e.g. Option A, Option B, Option C"
                            className="w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                          />
                        </div>
                      );
                    }

                    // Default: text input
                    return (
                      <div key={fieldKey}>
                        <label
                          htmlFor={`${idBase}-${index}-${fieldKey}`}
                          className="block text-[10.5px] font-semibold tracking-[0.04em] uppercase text-ink-4 mb-[5px]"
                        >
                          {fieldLabel(fieldKey)}
                        </label>
                        <input
                          id={`${idBase}-${index}-${fieldKey}`}
                          type="text"
                          value={(item[fieldKey] as string) ?? ""}
                          onChange={(e) => updateItemField(index, fieldKey, e.target.value)}
                          className="w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add item button */}
      <button
        type="button"
        onClick={addItem}
        disabled={atMax}
        title={atMax ? `Maximum of ${meta.maxItems} ${meta.itemLabel}s` : undefined}
        className="mt-2 flex items-center gap-1 text-[11.5px] font-medium text-tide hover:text-tide-d disabled:text-ink-5 disabled:cursor-not-allowed transition-colors"
      >
        <Plus size={13} strokeWidth={2.5} />
        Add {meta.itemLabel}
      </button>
      {atMax && (
        <p className="mt-0.5 text-[10.5px] text-ink-5">
          Maximum of {meta.maxItems} {meta.itemLabel}s reached.
        </p>
      )}
    </div>
  );
}
